import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface Group {
  id: string;
  name: string;
  description?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface InProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (assignmentGroupId: string | null, assignedUserId: string | null) => void;
  taskId: string;
  customerId?: string | null;
}

const InProgressModal: React.FC<InProgressModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  taskId: _taskId,
  customerId
}) => {
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [assignedUserId, setAssignedUserId] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Combobox search states
  const [groupQuery, setGroupQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [errors, setErrors] = useState<{ assignmentGroup?: string; assignedTo?: string }>({});

  // Filter groups based on search query
  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((group) =>
        group.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  // Filter users based on search query
  const filteredUsers = userQuery === ''
    ? users
    : users.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const query = userQuery.toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });

  // Get selected group and user objects
  const selectedGroup = groups.find(g => g.id === assignmentGroupId);
  const selectedUser = users.find(u => u.id === assignedUserId);

  // Fetch assignment groups
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await api.get('/groups', {
          params: {
            type: 'assignment',
            isActive: true,
            limit: 1000
          }
        });
        setGroups(response.data.groups || []);
      } catch (error) {
        console.error('Error fetching groups:', error);
        toast.error('Failed to load assignment groups');
      } finally {
        setLoadingGroups(false);
      }
    };

    if (isOpen) {
      fetchGroups();
      setErrors({});
    }
  }, [isOpen]);

  // Fetch users based on selected assignment group
  useEffect(() => {
    const fetchUsers = async () => {
      if (!assignmentGroupId) {
        setUsers([]);
        setAssignedUserId('');
        return;
      }

      setLoadingUsers(true);
      try {
        const members = await fetchAssignmentGroupMembers(assignmentGroupId, customerId || null);
        setUsers(members || []);
      } catch (error) {
        console.error('Error fetching group members:', error);
        toast.error('Failed to load group members');
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [assignmentGroupId, customerId]);

  // Clear validation errors when selections are made
  useEffect(() => {
    if (assignmentGroupId) {
      setErrors(prev => ({ ...prev, assignmentGroup: undefined }));
    }
  }, [assignmentGroupId]);

  useEffect(() => {
    if (assignedUserId) {
      setErrors(prev => ({ ...prev, assignedTo: undefined }));
    }
  }, [assignedUserId]);

  const handleSubmit = async () => {
    const newErrors: { assignmentGroup?: string; assignedTo?: string } = {};

    // Validate Assignment Group
    if (!assignmentGroupId) {
      newErrors.assignmentGroup = 'Assignment Group is required';
    }

    // Validate Assigned To
    if (!assignedUserId) {
      newErrors.assignedTo = 'Assigned To is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm(
        assignmentGroupId || null,
        assignedUserId || null
      );
      handleClose();
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setAssignmentGroupId('');
    setAssignedUserId('');
    setUsers([]);
    setGroupQuery('');
    setUserQuery('');
    setErrors({});
    onClose();
  };

  const handleGroupChange = (groupId: string) => {
    setAssignmentGroupId(groupId);
    setAssignedUserId(''); // Reset user when group changes
    setUserQuery('');
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all overflow-visible">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Move to In Progress
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-4 overflow-visible">
                  {/* Assignment Group Combobox */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                    <Combobox value={assignmentGroupId} onChange={handleGroupChange}>
                      <div className="relative">
                        <div className={`relative w-full cursor-default overflow-hidden rounded-md border bg-white text-left shadow-sm ${
                          errors.assignmentGroup
                            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
                            : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'
                        }`}>
                          <Combobox.Input
                            className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                            displayValue={() => selectedGroup?.name || ''}
                            onChange={(event) => setGroupQuery(event.target.value)}
                            placeholder="Search assignment groups..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </Combobox.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {loadingGroups ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                Loading groups...
                              </div>
                            ) : filteredGroups.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                {groupQuery ? 'No groups found.' : 'No assignment groups available.'}
                              </div>
                            ) : (
                              filteredGroups.map((group) => (
                                <Combobox.Option
                                  key={group.id}
                                  className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                  value={group.id}
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span
                                        className={`block truncate ${
                                          selected ? 'font-medium' : 'font-normal'
                                        }`}
                                      >
                                        {group.name}
                                      </span>
                                      {selected ? (
                                        <span
                                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-blue-600'
                                          }`}
                                        >
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignmentGroup && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignmentGroup}</p>
                    )}
                  </div>

                  {/* Assigned To Combobox */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To <span className="text-red-500">*</span>
                    </label>
                    <Combobox
                      value={assignedUserId}
                      onChange={(value: string | null) => setAssignedUserId(value || '')}
                      disabled={!assignmentGroupId || loadingUsers}
                    >
                      <div className="relative">
                        <div className={`relative w-full cursor-default overflow-hidden rounded-md border bg-white text-left shadow-sm ${
                          errors.assignedTo
                            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
                            : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'
                        } ${!assignmentGroupId || loadingUsers ? 'bg-gray-100' : ''}`}>
                          <Combobox.Input
                            className={`w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 ${
                              !assignmentGroupId || loadingUsers ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            displayValue={() =>
                              selectedUser
                                ? `${selectedUser.firstName} ${selectedUser.lastName}`
                                : ''
                            }
                            onChange={(event) => setUserQuery(event.target.value)}
                            placeholder={
                              !assignmentGroupId
                                ? 'Select an assignment group first'
                                : loadingUsers
                                ? 'Loading users...'
                                : 'Search users...'
                            }
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </Combobox.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setUserQuery('')}
                        >
                          <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {loadingUsers ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                Loading users...
                              </div>
                            ) : filteredUsers.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                {userQuery ? 'No users found.' : 'No members in this group.'}
                              </div>
                            ) : (
                              filteredUsers.map((user) => (
                                <Combobox.Option
                                  key={user.id}
                                  className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                  value={user.id}
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <div className="flex flex-col">
                                        <span
                                          className={`block truncate ${
                                            selected ? 'font-medium' : 'font-normal'
                                          }`}
                                        >
                                          {user.firstName} {user.lastName}
                                        </span>
                                        <span
                                          className={`block truncate text-xs ${
                                            active ? 'text-blue-200' : 'text-gray-500'
                                          }`}
                                        >
                                          {user.email}
                                        </span>
                                      </div>
                                      {selected ? (
                                        <span
                                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-blue-600'
                                          }`}
                                        >
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                    {!errors.assignedTo && !assignmentGroupId && (
                      <p className="mt-1 text-xs text-gray-500">Please select an assignment group first</p>
                    )}
                    {!errors.assignedTo && assignmentGroupId && !loadingUsers && users.length === 0 && (
                      <p className="mt-1 text-xs text-yellow-600">No members found in this group</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InProgressModal;
